/**
 * WebrequestClient - Advanced configuration and global interceptors
 */

import type {
  IWebrequestJsonResult,
  IWebrequestOptions,
  IWebrequestResponseMetadata,
  IWebrequestResponseResult,
} from './webrequest.types.js';
import type {
  TRequestInterceptor,
  TResponseInterceptor,
  TErrorInterceptor,
} from './interceptors/interceptor.types.js';
import { InterceptorManager } from './interceptors/interceptor.manager.js';
import { CacheManager } from './cache/cache.manager.js';
import type { IStrategyResult } from './cache/cache.strategies.js';
import { RetryManager } from './retry/retry.manager.js';
import { RequestDeduplicator } from './utils/deduplicator.js';
import {
  consumeJsonWithTimeout,
  createTimeoutError,
  fetchWithTimeout,
} from './utils/timeout.js';

export class WebrequestClient {
  private interceptorManager: InterceptorManager;
  private cacheManager: CacheManager;
  private deduplicator: RequestDeduplicator;
  private defaultOptions: Partial<IWebrequestOptions>;
  private readonly closeReason = new Error('Webrequest client is closed');
  private disposed = false;
  private closePromise?: Promise<void>;

  constructor(options: Partial<IWebrequestOptions> = {}) {
    this.defaultOptions = options;
    this.interceptorManager = new InterceptorManager();
    this.cacheManager = new CacheManager();
    this.deduplicator = new RequestDeduplicator();
  }

  /**
   * Add a global request interceptor
   */
  public addRequestInterceptor(interceptor: TRequestInterceptor): void {
    this.interceptorManager.addRequestInterceptor(interceptor);
  }

  /**
   * Add a global response interceptor
   */
  public addResponseInterceptor(interceptor: TResponseInterceptor): void {
    this.interceptorManager.addResponseInterceptor(interceptor);
  }

  /**
   * Add a global error interceptor
   */
  public addErrorInterceptor(interceptor: TErrorInterceptor): void {
    this.interceptorManager.addErrorInterceptor(interceptor);
  }

  /**
   * Remove a request interceptor
   */
  public removeRequestInterceptor(interceptor: TRequestInterceptor): void {
    this.interceptorManager.removeRequestInterceptor(interceptor);
  }

  /**
   * Remove a response interceptor
   */
  public removeResponseInterceptor(interceptor: TResponseInterceptor): void {
    this.interceptorManager.removeResponseInterceptor(interceptor);
  }

  /**
   * Remove an error interceptor
   */
  public removeErrorInterceptor(interceptor: TErrorInterceptor): void {
    this.interceptorManager.removeErrorInterceptor(interceptor);
  }

  /**
   * Clear all interceptors
   */
  public clearInterceptors(): void {
    this.interceptorManager.clearAll();
  }

  /**
   * Clear the cache
   */
  public async clearCache(): Promise<void> {
    await this.cacheManager.clear();
  }

  /**
   * Delete a specific cache entry
   */
  public async deleteCache(cacheKey: string): Promise<void> {
    await this.cacheManager.delete(cacheKey);
  }

  /**
   * Execute a request with all configured features
   */
  public async request(
    url: string | Request,
    options: IWebrequestOptions = {},
  ): Promise<Response> {
    return (await this.requestWithMetadata(url, options)).response;
  }

  /**
   * Execute a request and return trusted cache-origin metadata separately from
   * the response headers.
   */
  public async requestWithMetadata(
    url: string | Request,
    options: IWebrequestOptions = {},
  ): Promise<IWebrequestResponseResult> {
    if (this.disposed) {
      throw this.closeReason;
    }
    // Merge default options with request options
    const mergedOptions: IWebrequestOptions = {
      ...this.defaultOptions,
      ...options,
    };

    // Create Request object
    let request: Request;
    if (typeof url === 'string') {
      request = new Request(url, mergedOptions);
    } else {
      request = url;
    }

    // Process through request interceptors
    request = await this.interceptorManager.processRequest(request);

    // Add per-request interceptors if provided
    if (mergedOptions.interceptors?.request) {
      for (const interceptor of mergedOptions.interceptors.request) {
        request = await interceptor(request);
      }
    }

    // Execute with deduplication if enabled
    const deduplicate = mergedOptions.deduplicate ?? false;

    if (deduplicate) {
      const dedupeKey = this.deduplicator.generateKey(request);
      const result = await this.deduplicator.execute(dedupeKey, async () => {
        return await this.executeRequest(request, mergedOptions);
      });
      return result.response;
    }

    return await this.executeRequest(request, mergedOptions);
  }

  /**
   * Internal request execution with caching and retry
   */
  private async executeRequest(
    request: Request,
    options: IWebrequestOptions,
  ): Promise<IWebrequestResponseResult> {
    try {
      // Determine if retry is enabled
      const retryOptions =
        typeof options.retry === 'object'
          ? options.retry
          : options.retry
            ? {}
            : undefined;

      // Create fetch function for Request objects (used with caching)
      const fetchFnForRequest = async (req: Request): Promise<Response> => {
        const timeout = options.timeout ?? 60000;
        const requestInit: RequestInit = { ...options };
        // Keep the final Request's buffered body source. Re-exposing req.body
        // creates a streaming upload that Chromium refuses over HTTP/1.1.
        delete requestInit.body;
        delete requestInit.headers;
        delete requestInit.method;
        delete requestInit.signal;
        return await fetchWithTimeout(
          req,
          { ...requestInit, signal: req.signal },
          timeout,
        );
      };

      // Create fetch function for fallbacks (url + init)
      const fetchFnForFallbacks = async (url: string, init: RequestInit): Promise<Response> => {
        const timeout = options.timeout ?? 60000;
        return await fetchWithTimeout(url, init, timeout);
      };

      let strategyResult: IStrategyResult;

      // Execute with retry if enabled
      if (retryOptions) {
        const retryManager = new RetryManager(retryOptions, options.signal);

        // Handle fallback URLs if provided
        if (options.fallbackUrls && options.fallbackUrls.length > 0) {
          const allUrls = [request.url, ...options.fallbackUrls];
          const response = await retryManager.executeWithFallbacks(
            allUrls,
            {
              method: request.method,
              headers: request.headers,
              body: request.body,
              ...options,
            },
            fetchFnForFallbacks,
          );
          strategyResult = {
            response,
            fromCache: false,
            revalidated: false,
          };
        } else {
          let latestResult: IStrategyResult | undefined;
          const response = await retryManager.execute(async () => {
            // Execute with caching
            const result = await this.cacheManager.execute(
              request,
              options,
              fetchFnForRequest,
            );
            latestResult = result;
            return result.response;
          });
          strategyResult =
            latestResult?.response === response
              ? latestResult
              : {
                  response,
                  fromCache: false,
                  revalidated: false,
                };
        }
      } else {
        // Execute with caching (no retry)
        strategyResult = await this.cacheManager.execute(
          request,
          options,
          fetchFnForRequest,
        );
      }

      // Process through response interceptors
      let response = await this.interceptorManager.processResponse(
        strategyResult.response,
      );

      // Add per-request response interceptors if provided
      if (options.interceptors?.response) {
        for (const interceptor of options.interceptors.response) {
          response = await interceptor(response);
        }
      }

      return createResponseResult(response, strategyResult);
    } catch (error) {
      // Process through error interceptors
      const processedError = await this.interceptorManager.processError(
        error instanceof Error ? error : new Error(String(error)),
      );

      throw processedError;
    }
  }

  /**
   * Stop owned background cache work and close persistent cache resources.
   */
  public async close(): Promise<void> {
    if (this.closePromise) {
      return this.closePromise;
    }
    this.disposed = true;
    this.closePromise = this.cacheManager.close();
    return this.closePromise;
  }

  /**
   * Execute a JSON request. With retries disabled, timeout is a call-wide
   * deadline across headers and body. Retry-enabled requests preserve the
   * existing per-attempt timeout and apply the same budget to the final body.
   */
  private async requestJsonWithMetadata<T>(
    url: string,
    options: IWebrequestOptions,
  ): Promise<IWebrequestJsonResult<T>> {
    const effectiveOptions: IWebrequestOptions = {
      ...this.defaultOptions,
      ...options,
    };
    const timeoutMs = effectiveOptions.timeout ?? 60_000;
    const callWideDeadline =
      !effectiveOptions.retry
        ? performance.now() + timeoutMs
        : undefined;
    const callWideController =
      callWideDeadline === undefined ? undefined : new AbortController();
    const requestSignal = callWideController
      ? effectiveOptions.signal
        ? AbortSignal.any([
            effectiveOptions.signal,
            callWideController.signal,
          ])
        : callWideController.signal
      : effectiveOptions.signal;
    const callWideTimeout =
      callWideController === undefined
        ? undefined
        : setTimeout(() => {
            callWideController.abort(createTimeoutError(url, timeoutMs));
          }, Math.max(0, timeoutMs));

    try {
      const result = await this.requestWithMetadata(url, {
        ...options,
        signal: requestSignal,
      });
      const response = result.response;

      if (requestSignal?.aborted) {
        await response.body?.cancel(requestSignal.reason).catch(() => undefined);
        throw requestSignal.reason instanceof Error
          ? requestSignal.reason
          : new Error('The request was aborted');
      }

      if (!response.ok) {
        await response.body
          ?.cancel(`HTTP ${response.status}`)
          .catch(() => undefined);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const bodyTimeoutMs =
        callWideDeadline === undefined
          ? timeoutMs
          : callWideDeadline - performance.now();

      const data = await consumeJsonWithTimeout<T>(response, {
        bodyTimeoutMs,
        reportedTimeoutMs: timeoutMs,
        signal: requestSignal,
        url,
      });
      return Object.freeze({
        data,
        response,
        metadata: result.metadata,
      });
    } catch (error) {
      if (requestSignal?.aborted && requestSignal.reason instanceof Error) {
        throw requestSignal.reason;
      }
      throw error;
    } finally {
      if (callWideTimeout !== undefined) {
        clearTimeout(callWideTimeout);
      }
    }
  }

  /**
   * Convenience method: GET request returning JSON
   */
  public async getJson<T = any>(
    url: string,
    options: IWebrequestOptions = {},
  ): Promise<T> {
    return (await this.getJsonWithMetadata<T>(url, options)).data;
  }

  /**
   * Convenience method: GET request returning JSON and response metadata
   */
  public async getJsonWithMetadata<T = any>(
    url: string,
    options: IWebrequestOptions = {},
  ): Promise<IWebrequestJsonResult<T>> {
    return await this.requestJsonWithMetadata<T>(url, {
      ...options,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...((options.headers as any) || {}),
      },
    });
  }

  /**
   * Convenience method: POST request with JSON body
   */
  public async postJson<T = any>(
    url: string,
    data: any,
    options: IWebrequestOptions = {},
  ): Promise<T> {
    return (await this.postJsonWithMetadata<T>(url, data, options)).data;
  }

  /**
   * Convenience method: POST request with JSON body and response metadata
   */
  public async postJsonWithMetadata<T = any>(
    url: string,
    data: any,
    options: IWebrequestOptions = {},
  ): Promise<IWebrequestJsonResult<T>> {
    return await this.requestJsonWithMetadata<T>(url, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...((options.headers as any) || {}),
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * Convenience method: PUT request with JSON body
   */
  public async putJson<T = any>(
    url: string,
    data: any,
    options: IWebrequestOptions = {},
  ): Promise<T> {
    return (await this.putJsonWithMetadata<T>(url, data, options)).data;
  }

  /**
   * Convenience method: PUT request with JSON body and response metadata
   */
  public async putJsonWithMetadata<T = any>(
    url: string,
    data: any,
    options: IWebrequestOptions = {},
  ): Promise<IWebrequestJsonResult<T>> {
    return await this.requestJsonWithMetadata<T>(url, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...((options.headers as any) || {}),
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * Convenience method: DELETE request
   */
  public async deleteJson<T = any>(
    url: string,
    options: IWebrequestOptions = {},
  ): Promise<T> {
    return (await this.deleteJsonWithMetadata<T>(url, options)).data;
  }

  /**
   * Convenience method: DELETE request returning JSON and response metadata
   */
  public async deleteJsonWithMetadata<T = any>(
    url: string,
    options: IWebrequestOptions = {},
  ): Promise<IWebrequestJsonResult<T>> {
    return await this.requestJsonWithMetadata<T>(url, {
      ...options,
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        ...((options.headers as any) || {}),
      },
    });
  }
}

const createResponseResult = (
  responseArg: Response,
  strategyResultArg: Pick<IStrategyResult, 'fromCache' | 'revalidated'>,
): IWebrequestResponseResult => {
  const metadata: IWebrequestResponseMetadata = Object.freeze({
    fromCache: strategyResultArg.fromCache,
    revalidated: strategyResultArg.revalidated,
  });
  return Object.freeze({
    response: responseArg,
    metadata,
  });
};
